"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_CANVAS_PLACEMENT_GAP = void 0;
exports.createRightSideBranchCandidates = createRightSideBranchCandidates;
exports.findFreeCanvasRect = findFreeCanvasRect;
exports.findFreeCanvasRowRects = findFreeCanvasRowRects;
exports.DEFAULT_CANVAS_PLACEMENT_GAP = 160;
const MAX_SCAN_ROWS = 18;
const MAX_SCAN_COLUMNS = 18;
function isFinitePositive(value) {
    return Number.isFinite(value) && value > 0;
}
function normalizeRect(rect) {
    const [x, y, w, h] = rect;
    if (!Number.isFinite(x) ||
        !Number.isFinite(y) ||
        !isFinitePositive(w) ||
        !isFinitePositive(h)) {
        return null;
    }
    return [x, y, w, h];
}
function preferredRect(position, width, height) {
    if (!position)
        return null;
    const { x, y } = position;
    if (!Number.isFinite(x) || !Number.isFinite(y))
        return null;
    return [x, y, width, height];
}
/**
 * Returns branch slots in visual reading order: centred, below, above, then
 * alternating farther away from the anchor. Collision checks stay in
 * findFreeCanvasRect so every caller uses the same overlap semantics.
 */
function createRightSideBranchCandidates(input) {
    const [anchorX, anchorY, anchorWidth, anchorHeight] = input.anchor;
    const baseX = anchorX + anchorWidth + input.gapX;
    const baseY = anchorY + (anchorHeight - input.height) / 2;
    const stepY = input.height + input.gapY;
    const maxDistance = Math.max(0, Math.floor(input.maxDistance ?? 18));
    const candidates = [{ x: baseX, y: baseY }];
    for (let distance = 1; distance <= maxDistance; distance++) {
        candidates.push({ x: baseX, y: baseY + distance * stepY });
        candidates.push({ x: baseX, y: baseY - distance * stepY });
    }
    return candidates;
}
function intersects(a, b, gap) {
    const [ax, ay, aw, ah] = a;
    const [bx, by, bw, bh] = b;
    return (ax < bx + bw + gap &&
        ax + aw + gap > bx &&
        ay < by + bh + gap &&
        ay + ah + gap > by);
}
function isFree(candidate, occupied, gap) {
    return !occupied.some(rect => intersects(candidate, rect, gap));
}
function union(rects) {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const [x, y, w, h] of rects) {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x + w);
        maxY = Math.max(maxY, y + h);
    }
    return [minX, minY, maxX - minX, maxY - minY];
}
function sortByReadingOrder(a, b) {
    return a[1] - b[1] || a[0] - b[0];
}
function uniqueCandidates(candidates) {
    const seen = new Set();
    return candidates.filter(candidate => {
        const key = candidate.map(value => Math.round(value)).join(':');
        if (seen.has(key))
            return false;
        seen.add(key);
        return true;
    });
}
function findFreeCanvasRect(input) {
    const width = isFinitePositive(input.width) ? input.width : 100;
    const height = isFinitePositive(input.height) ? input.height : 100;
    const gap = input.gap ?? exports.DEFAULT_CANVAS_PLACEMENT_GAP;
    const occupied = input.occupied
        .map(normalizeRect)
        .filter((rect) => !!rect)
        .sort(sortByReadingOrder);
    const preferred = [input.preferred, ...(input.preferredCandidates ?? [])]
        .map(position => preferredRect(position, width, height))
        .filter((rect) => !!rect);
    const freePreferred = preferred.find(candidate => isFree(candidate, occupied, gap));
    if (freePreferred) {
        return freePreferred;
    }
    if (occupied.length === 0) {
        return [gap, gap, width, height];
    }
    const [minX, minY, totalW, totalH] = union(occupied);
    const maxX = minX + totalW;
    const maxY = minY + totalH;
    const candidates = [
        [maxX + gap, minY + totalH / 2 - height / 2, width, height],
    ];
    for (const [x, y, w, h] of occupied) {
        candidates.push([x + w + gap, y, width, height]);
        candidates.push([x, y + h + gap, width, height]);
    }
    const stepX = width + gap;
    const stepY = height + gap;
    const gridStartX = maxX + gap;
    const gridStartY = minY;
    for (let row = 0; row < MAX_SCAN_ROWS; row++) {
        for (let column = 0; column < MAX_SCAN_COLUMNS; column++) {
            candidates.push([
                gridStartX + column * stepX,
                gridStartY + row * stepY,
                width,
                height,
            ]);
        }
    }
    const aboveStartY = minY - height - gap;
    for (let row = 0; row < MAX_SCAN_ROWS; row++) {
        for (let column = 0; column < MAX_SCAN_COLUMNS; column++) {
            candidates.push([
                minX + column * stepX,
                aboveStartY - row * stepY,
                width,
                height,
            ]);
        }
    }
    return (uniqueCandidates(candidates).find(candidate => isFree(candidate, occupied, gap)) ?? [maxX + gap, maxY + gap, width, height]);
}
/**
 * Places a related group as one collision-checked horizontal row. Resolving
 * the group bounds first keeps every child on the same y baseline even when
 * unrelated canvas content forces the preferred position to move.
 */
function findFreeCanvasRowRects(input) {
    if (input.items.length === 0)
        return [];
    const gap = input.gap ?? exports.DEFAULT_CANVAS_PLACEMENT_GAP;
    const items = input.items.map(item => ({
        width: isFinitePositive(item.width) ? item.width : 100,
        height: isFinitePositive(item.height) ? item.height : 100,
    }));
    const groupWidth = items.reduce((total, item) => total + item.width, 0) +
        gap * (items.length - 1);
    const groupHeight = Math.max(...items.map(item => item.height));
    const [groupX, groupY] = findFreeCanvasRect({
        occupied: input.occupied,
        width: groupWidth,
        height: groupHeight,
        gap,
        preferred: input.preferred,
    });
    let x = groupX;
    return items.map(item => {
        const rect = [x, groupY, item.width, item.height];
        x += item.width + gap;
        return rect;
    });
}
